import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormProvider from "../../hooks/form/form-provider";
import Button from "../button";
import Label from "../label";
import Modal from "../modal";
import TextAreaCounter from "../textarea-with-counter";
import AwardsPreview from '../award-preview';
import SpotlightPreview from '../spotlight-preview';
import { handleDownloadImage } from '../../helpers/get-file-image';
import S3Helper from '../../helpers/s3-helper';
import RHFFileInput from '../file-input/rhf-file-input';
import moment from 'moment';
import RHFEditor from '../editor/rhf-editor';
import PublishCountService from '../../services/publish-count-service';
import GroupSpotlightPreview from '../group-spotlight-preview';
import { groupLogo } from '../../constants/constants';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose?: any;
    title: string;
    submitButtonText: string;
    submitButtonIcon?: any;
    submitButton: any;
    submitButtonVariant: any;
    isRequiredDescription: boolean;
    descriptionLabel?: string;
    textAreaMaxLength?: number;
    noOfTextareaRows?: number;
    type: string;
    item?: any;
    descriptionContent?: string;
    isLoadingConfirmModal?: boolean;
    setLoadingConfirmModal: any;
    requiredFieldErrorMessage?: string;
    setPreview?: any;
    isPreview?: boolean;
}

function ConfirmModal({ isOpen, onClose, title, submitButtonText, submitButtonIcon, submitButton, submitButtonVariant = "endorsed", isRequiredDescription, descriptionLabel = "Details", noOfTextareaRows = 3, textAreaMaxLength = 100, type, item, descriptionContent = "", isLoadingConfirmModal = false, setLoadingConfirmModal, requiredFieldErrorMessage, setPreview, isPreview }: ConfirmModalProps) {
    const divRef = useRef<HTMLDivElement>(null);
    const [publishedCount, setPublishedCount] = useState<any>(null);
    const [files, setFiles] = useState<File[]>([]);

    const ConfirmModalSchema = useMemo(() => {
        return Yup.object().shape({
            description: isRequiredDescription
                ? Yup.string().required(requiredFieldErrorMessage ? requiredFieldErrorMessage : 'Detail is required').min(3, 'Minimum 3 characters required')
                : Yup.string(), // Optional when isRequiredDescription is false
            teamLogo: item?.nomination_type === 'project' && submitButtonText === 'Publish' && item?.nominee?.logo_url === ''
                ? Yup.mixed()
                    .test('required', 'Team Logo is required', (value) => {
                        return value instanceof File || (Array.isArray(value) && value.length > 0);
                    })
                : Yup.mixed(),
        });
    }, [isRequiredDescription, item, submitButtonText, requiredFieldErrorMessage]);

    const defaultValues = useMemo(() => ({
        description: descriptionContent || '',
        teamLogo: ''
    }), [descriptionContent]);

    const methods = useForm({
        resolver: yupResolver(ConfirmModalSchema),
        defaultValues,
    });

    const {
        watch,
        reset,
        handleSubmit,
        // formState: { isSubmitting },
    } = methods;

    const getApproximateZoomLevel = () => {
        return window.outerWidth / window.innerWidth;
    };


    const onSubmit = handleSubmit(async (data) => {
        setLoadingConfirmModal(true);
        if (submitButtonText === 'Publish') {
            setPreview(true);
            const attachment: any = await handleDownloadImage(divRef, `${item?.nominee?.name || ''}`);
            let updatedFiles = [];
            if (attachment !== null) {
                const lastType = attachment.name?.split('.');
                const s3UnSignedPayload = {
                    filename: `${lastType[0]}___${moment().unix()}.${lastType[lastType?.length - 1]}`,
                    filetype: attachment?.type,
                };

                const presignedUrls = await S3Helper.GetS3PresignedUrl([s3UnSignedPayload]);

                // Process each item with presigned URLs
                updatedFiles = await Promise.all(
                    presignedUrls.map(async (itemObj: any) => {
                        if (attachment) {
                            const originalFile = attachment;
                            // Create a new File object with a modified name
                            const newFile = new File([originalFile], `${s3UnSignedPayload.filename}${s3UnSignedPayload.filetype}`, {
                                type: originalFile.type,
                                lastModified: originalFile.lastModified,
                            });
                            // Upload file using the presigned URL
                            const s3Url = await S3Helper.UploadFileToS3(itemObj, newFile, s3UnSignedPayload.filetype);
                            attachment.s3Url = s3Url;
                        }
                        return attachment;
                    })
                );
            }

            const mediaPayload = updatedFiles.map((item: any) => {
                const response: any = {};
                response.name = item.name;
                response.url = item.s3Url;
                response.size = item.size;
                response.type = item.type;
                return response;
            });

            let updatedTeamFiles = [];
            let teamMediaPayload = [];

            if (item?.nomination_type !== 'individual' && data?.teamLogo?.[0]) {

                const logo = data.teamLogo?.[0];
                if (data.teamLogo !== null) {
                    const s3UnSignedPayload = {
                        filename: logo?.name,
                        filetype: logo?.type
                    };

                    const presignedUrls = await S3Helper.GetS3PresignedUrl([s3UnSignedPayload]);

                    // Process each item with presigned URLs
                    updatedTeamFiles = await Promise.all(
                        presignedUrls.map(async (itemObj: any) => {
                            if (logo) {
                                // Upload file using the presigned URL
                                const s3Url = await S3Helper.UploadFileToS3(itemObj, logo, s3UnSignedPayload.filetype);
                                logo.s3Url = s3Url;
                            }
                            return logo;
                        })
                    );
                }

                teamMediaPayload = updatedTeamFiles.map((item: any) => {
                    const response: any = {};
                    response.name = item.name;
                    response.url = item.s3Url;
                    response.size = item.size;
                    response.type = item.type;
                    return response;
                });
            }
            const payload: any = {
                data: data,
                s3Url: mediaPayload?.[0]?.url || ''
            }

            if (item?.nomination_type === 'project') {
                payload.logoUrl = teamMediaPayload?.[0]?.url ? teamMediaPayload?.[0]?.url : item?.nominee?.logo_url
            }
            if (item?.nomination_type === 'group') {
                payload.logoUrl = teamMediaPayload?.[0]?.url ? teamMediaPayload?.[0]?.url : (item?.nomination_projects?.[0]?.project?.logo_url || groupLogo)
            }
            submitButton(payload);
        }
        else {
            submitButton({ data: data });
        }
    });

    // Reset form state when modal is closed
    useEffect(() => {
        if (!isOpen) {
            reset(defaultValues);
        }
    }, [isOpen, reset, defaultValues]);

    const fetchPublishCount = useCallback(async () => {
        try {
            const response = await PublishCountService.getPublishCount();
            setPublishedCount(response?.published_count);
        } catch (error) {
            console.log(error);
        }
    }, []);

    useEffect(() => {
        if (isOpen)
            fetchPublishCount();
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Ensure `description` updates when `descriptionContent` changes
    useEffect(() => {
        reset({ description: descriptionContent || '', teamLogo: '' });
    }, [descriptionContent, reset]);

    const groupLogoUrl = item?.nomination_type === 'group' ? item?.nomination_projects?.[0]?.project?.logo_url || groupLogo : item?.nominee?.logo_url || '';

    return (
        <Modal isOpen={isOpen}
            onClose={() => { onClose(); setPreview(false); }} size="xs" className={isPreview && submitButtonText === 'Publish' ? type === 'wall of fame' ? "!w-[70%] scale-[0.825]" : type === 'spotlight' ? "!w-[100%] max-w-[50rem]" : "" : ""} >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <div className="text-left py-4 scroll">
                    <h4 className="text-2xl font-bold font-aeonikBold">{isPreview ? "Publish preview" : title}</h4>
                    {getApproximateZoomLevel() !== 1 && submitButtonText === 'Publish' ?
                        <div className="text-xs text-rejectDark">
                            Please set your browser zoom level to 100% before publishing.
                        </div>
                        : null
                    }
                    {submitButtonText === 'Publish' &&
                        (type === 'wall of fame' ? publishedCount?.wof > 0 : publishedCount?.spotlight > 0) ? (
                        <div className="text-xs text-rejectDark">{`(`}
                            {type === 'wall of fame' ? publishedCount?.wof : publishedCount?.spotlight}
                            {type === 'wall of fame' ? (publishedCount?.wof === 1 ? " record " : " records ") : (publishedCount?.spotlight === 1 ? " record " : " records ")}
                            already published this month{`)`}
                        </div>
                    ) : null
                    }

                    {
                        submitButtonText === 'Publish' ?
                            type === 'wall of fame' ?
                                <div className='wall-fame-wrap'>
                                    <div ref={divRef} className={isPreview ? "" : "hidden"}>
                                        <AwardsPreview
                                            date={item.nomination_month}
                                            recognitionFor={item.nomination_reason}
                                            description={watch().description}
                                            projects={item.nomination_projects}
                                            nomineeUser={item?.nominee}
                                        /></div>  </div> :
                                <div className='wall-fame-wrap' style={{ maxHeight: '72vh' }}>
                                    <div ref={divRef} className={isPreview ? "" : "hidden"}>
                                        {item.nomination_type === 'individual' ?
                                            <SpotlightPreview
                                                recognitionFor={item.nomination_reason}
                                                description={watch().description}
                                                nomineeUser={item?.nominee}
                                                file={watch().teamLogo?.[0] || ''}
                                                projects={item.nomination_projects}
                                                nominationType={item?.nomination_type}
                                            /> :
                                            <GroupSpotlightPreview
                                                recognitionFor={item.nomination_reason}
                                                description={watch().description}
                                                nomineeUser={item?.nominee}
                                                file={watch().teamLogo?.[0] || ''}
                                                projects={item.nomination_projects}
                                                nominationType={item?.nomination_type}
                                                members={item?.members}
                                            />
                                        }
                                    </div></div> : ''}
                    {submitButtonText === 'Publish' && isPreview ?
                        <div className="text-xs mb-3 text-darkGrey">
                            Please limit your post to approximately 5-7 lines for better readability.
                        </div>
                        : null
                    }

                    <div className={`${isPreview ? "hidden" : ""}`}>
                        {(item?.nomination_type !== 'individual' && submitButtonText === 'Publish') ? <div>
                            <Label label={<span>{"Upload Logo"}<span className='text-rejectDark'>*</span></span>} />
                            <RHFFileInput files={files} setFiles={setFiles} name={'teamLogo'} maxFileLength={1} className='!w-full !grid-cols-2 ' logoURL={groupLogoUrl} logoURLName={item?.nominee?.name || 'Group Image'} isReplaceIcon={true} showBrowseButton={false} />
                        </div> : null}
                        <div className="my-1">
                            <Label label={isRequiredDescription ? <span>{descriptionLabel}<span className='text-rejectDark'>*</span></span> : <span>{descriptionLabel}<span className='lowercase text-xs !text-gray-500'>{` (optional)`}</span></span>} />
                            {submitButtonText === "Publish" ?
                                <RHFEditor
                                    name="description"
                                    containerClassName="relative"
                                    editorClassName="h-44"
                                    maxLength={1000}
                                />
                                :
                                <TextAreaCounter
                                    name='description'
                                    maxLength={textAreaMaxLength}
                                    placeholder="Add details here"
                                    rows={noOfTextareaRows}
                                    disabled={isLoadingConfirmModal}
                                />}


                        </div>
                    </div>


                    <div className='flex flex-row justify-between'>
                        {submitButtonText === "Publish" ? <div>
                            <Button
                                buttonText={isPreview ? "Back to Editing" : "Preview"}
                                btnSize="md"
                                btnLabel
                                onClick={(e: any) => { e.preventDefault(); e.stopPropagation(); setPreview(!isPreview) }}
                                disabled={isLoadingConfirmModal || watch().description === '' || (((item?.nominee?.logo_url === '' || item?.nominee?.logo_url === null) && watch().teamLogo === '' && item.nomination_type === 'project'))}
                                btnColor="white-btn"
                                className="!w-full"
                                btnIcon={false}
                            />
                        </div> : <div />}

                        <div className="flex justify-end">
                            {/* {<Button
                                buttonText="Cancel"
                                btnSize="md"
                                btnLabel
                                iconStyleClassName={"mr-2"}
                                onClick={() => { onClose(); setPreview(false); }}
                                disabled={isLoadingConfirmModal}
                                btnColor="endorsed"
                                className="mr-2"
                            />} */}

                            {submitButtonText === "Publish" && isPreview ? <Button
                                buttonText={submitButtonText}
                                btnSize="md"
                                btnIcon={!isLoadingConfirmModal ? true : false}
                                btnLabel
                                IconSvg={submitButtonIcon}
                                iconStyleClassName={"mr-2"}
                                disabled={isLoadingConfirmModal}
                                btnColor={submitButtonVariant}
                                type="submit"
                                loading={isLoadingConfirmModal}
                            /> :
                                submitButtonText !== "Publish" ?
                                    <Button
                                        buttonText={submitButtonText}
                                        btnSize="md"
                                        btnIcon={!isLoadingConfirmModal ? true : false}
                                        btnLabel
                                        IconSvg={submitButtonIcon}
                                        iconStyleClassName={"mr-2"}
                                        disabled={isLoadingConfirmModal}
                                        btnColor={submitButtonVariant}
                                        type="submit"
                                        loading={isLoadingConfirmModal}
                                    /> : null
                            }
                        </div>
                    </div>
                </div>
            </FormProvider>
        </Modal>
    );
};

export default memo(ConfirmModal);
